<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center">
          <h1 class="text-h4 mb-4">{{ $t('coins.title') }}</h1>
          <div class="d-flex align-center">
            <v-text-field
              v-model="coinFilter"
              :label="$t('coins.filter')"
              prepend-inner-icon="mdi-magnify"
              density="compact"
              hide-details
              class="mr-3"
              style="max-width: 320px; min-width: 220px;"
              clearable
            />
            <v-btn color="primary" @click="openAddDialog">{{ $t('coins.add') }}</v-btn>
          </div>
        </div>
      </v-col>
    </v-row>
    
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>{{ $t('coins.list') }}</v-card-title>
          <v-card-text>
            <v-table density="compact">
              <thead>
                <tr>
                  <th>{{ $t('coins.id') }}</th>
                  <th>{{ $t('coins.symbol') }}</th>
                  <th>{{ $t('coins.name') }}</th>
                  <th>{{ $t('coins.fiat') }}</th>
                  <th>{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr 
                  v-for="coin in filteredCoins" 
                  :key="coin.id"
                  style="cursor: pointer;"
                  @click="goToCoinRates(coin.id)"
                  class="hover-row"
                >
                  <td>{{ coin.id }}</td>
                  <td><strong>{{ coin.symbol }}</strong></td>
                  <td>{{ coin.name }}</td>
                  <td>
                    <v-chip :color="coin.fiat ? 'primary' : 'default'" size="small">
                      {{ coin.fiat ? $t('coins.fiatYes') : $t('coins.fiatNo') }}
                    </v-chip>
                  </td>
                  <td @click.stop>
                    <v-btn variant="plain" size="small" @click="editCoin(coin)" icon="mdi-pencil"></v-btn>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="showAddDialog" max-width="500" @update:model-value="(val) => { if (!val) resetForm(); }">
      <v-card>
        <v-card-title>{{ coinForm.id ? $t('coins.edit') : $t('coins.add') }}</v-card-title>
        <v-card-text>
          <v-form>
            <v-text-field
              v-model="coinForm.symbol"
              :label="$t('coins.symbol')"
              :placeholder="$t('coins.symbolPlaceholder')"
              required
              :rules="[v => !!v || $t('coins.symbolRequired')]"
            />
            <v-text-field
              v-model="coinForm.name"
              :label="$t('coins.name')"
              :placeholder="$t('coins.namePlaceholder')"
              required
              :rules="[v => !!v || $t('coins.nameRequired')]"
              class="mt-2"
            />
            <v-checkbox
              v-model="coinForm.fiat"
              :label="$t('coins.fiat')"
              class="mt-2"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showAddDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveCoin">{{ coinForm.id ? $t('common.save') : $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showErrorDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6 text-error">
          <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
          {{ $t('errors.unknownError') }}
        </v-card-title>
        <v-card-text>
          <p class="text-body-1">{{ errorMessage }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showErrorDialog = false">{{ $t('errors.ok') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { apiGet, apiPost, apiPut } from '../utils/api'

const router = useRouter()
const { t } = useI18n()

interface Coin {
  id: number
  name: string
  symbol: string
  fiat?: boolean
}

const coins = ref<Coin[]>([])
const coinFilter = ref('')
const showAddDialog = ref(false)
const showErrorDialog = ref(false)
const errorMessage = ref('')
const coinForm = ref({
  id: null as number | null,
  name: '',
  symbol: '',
  fiat: false
})

const loadCoins = async () => {
  try {
    const response = await apiGet('/api/coins')
    if (response.ok) {
      coins.value = await response.json()
    }
  } catch (error) {
    console.error('Load coins error:', error)
  }
}

const filteredCoins = computed(() => {
  if (!coinFilter.value) return coins.value
  const search = coinFilter.value.toLowerCase()
  return coins.value.filter(c => 
    c.symbol.toLowerCase().includes(search) ||
    c.name.toLowerCase().includes(search)
  )
})

const openAddDialog = () => {
  resetForm()
  showAddDialog.value = true
}

const editCoin = (coin: Coin) => {
  coinForm.value = {
    id: coin.id,
    name: coin.name,
    symbol: coin.symbol,
    fiat: coin.fiat || false
  }
  showAddDialog.value = true
}

const resetForm = () => {
  coinForm.value = {
    id: null,
    name: '',
    symbol: '',
    fiat: false
  }
}

const goToCoinRates = (coinId: number) => {
  router.push(`/rates?coin_id=${coinId}`)
}

const showError = (message: string) => {
  errorMessage.value = message
  showErrorDialog.value = true
}

const saveCoin = async () => {
  if (!coinForm.value.name || !coinForm.value.symbol) {
    showError(t('coins.fillAllFields'))
    return
  }

  try {
    let response
    if (coinForm.value.id) {
      // Update existing coin
      const coinData = {
        name: coinForm.value.name,
        symbol: coinForm.value.symbol.toUpperCase(),
        fiat: coinForm.value.fiat || false
      }
      response = await apiPut(`/api/coins/${coinForm.value.id}`, { coin: coinData })
    } else {
      // Create new coin
      const coinData = {
        name: coinForm.value.name,
        symbol: coinForm.value.symbol.toUpperCase(),
        fiat: coinForm.value.fiat || false
      }
      response = await apiPost('/api/coins', { coin: coinData })
    }

    if (response.ok) {
      await loadCoins()
      showAddDialog.value = false
      resetForm()
    } else {
      const errorData = await response.json()
      const errorText = errorData.errors?.join(', ') || t('errors.unknownError')
      showError(t('coins.failedToSave', { errors: errorText }))
    }
  } catch (error) {
    console.error('Save coin error:', error)
    const errorText = error instanceof Error ? error.message : t('coins.errorSaving', { errors: error.message })
    showError(t('coins.errorSaving', { errors: errorText }))
  }
}

onMounted(() => {
  loadCoins()
})
</script>

<style scoped>
.hover-row:hover {
  background-color: rgba(0, 0, 0, 0.05);
}
</style>
